import React, { useEffect } from "react";
import Styles from "../../components/Report/Report.module.css";
import useFetchPegPage from "../../hooks/PhysicianReportPage.ts/useFetchPegPage";

interface ScoreSectionProps {
  patientId: string;
  billingCycleId: string;
  riskRatingData?: {
    verificationResult: string;
  } | null;
  riskRatingLoading?: boolean;
}

const NewScoreSection: React.FC<ScoreSectionProps> = ({
  patientId,
  billingCycleId,
  riskRatingData,
  riskRatingLoading = false,
}) => {
  const { pegData, pegLoading, refetch } = useFetchPegPage(billingCycleId);
  useEffect(() => {
    if (billingCycleId) {
      refetch();
    }
  }, [billingCycleId, refetch]);

  const hasRiskRatingData = riskRatingData && riskRatingData.verificationResult;
  const hasPegData = pegData && pegData.verificationResult;

  const getPegActiveClass = (level: string) => {
    if (!pegData?.verificationResult) return "";
    const currentLevel = pegData.verificationResult.toLowerCase();
    const mappedLevel = currentLevel === "moderate" ? "mod" : currentLevel;
    return mappedLevel === level.toLowerCase() ? Styles.active : "";
  };

  const getDisplayScore = (level: string) => {
    return level === "moderate" ? "MOD" : level.toUpperCase();
  };
  return (
    <div className={Styles.scoreSection}>
      <div className={Styles.scoreCard}>
        <div className={Styles.scoreCardTitle}>RISK RATING</div>
        {riskRatingLoading ? (
          <div className={Styles.scoreSectionLoading}>
            Loading risk rating...
          </div>
        ) : !hasRiskRatingData ? (
          <div className={Styles.scoreSectionNoData}>No data available</div>
        ) : (
          <div className={Styles.scoreButtons}>
            <span
              className={[
                Styles.scoreButton,
                Styles.scoreButtonRiskLow,
                riskRatingData?.verificationResult?.toLowerCase() === "low"
                  ? Styles.active
                  : "",
              ].join(" ")}
            >
              {riskRatingData?.verificationResult?.toLowerCase() === "low"
                ? "LOW"
                : "LOW"}
            </span>
            <span
              className={[
                Styles.scoreButton,
                Styles.scoreButtonRiskMod,
                riskRatingData?.verificationResult?.toLowerCase() === "moderate"
                  ? Styles.active
                  : "",
              ].join(" ")}
            >
              {riskRatingData?.verificationResult?.toLowerCase() === "moderate"
                ? "MOD"
                : "MOD"}
            </span>
            <span
              className={[
                Styles.scoreButton,
                Styles.scoreButtonRiskHigh,
                riskRatingData?.verificationResult?.toLowerCase() === "high"
                  ? Styles.active
                  : "",
              ].join(" ")}
            >
              {riskRatingData?.verificationResult?.toLowerCase() === "high"
                ? "HIGH"
                : "HIGH"}
            </span>
          </div>
        )}
      </div>
      <div className={Styles.scoreCard}>
        <div className={Styles.scoreCardTitle}>PEG SCORE</div>
        {pegLoading ? (
          <div className={Styles.scoreSectionLoading}>Loading PEG score...</div>
        ) : !hasPegData ? (
          <div className={Styles.scoreSectionNoData}>No data available</div>
        ) : (
          <div className={Styles.scoreButtons}>
            <span
              className={[
                Styles.scoreButton,
                Styles.scoreButtonPegLow,
                getPegActiveClass("low"),
              ]
                .join(" ")
                .trim()}
            >
              LOW
            </span>
            <span
              className={[
                Styles.scoreButton,
                Styles.scoreButtonPegMod,
                getPegActiveClass("mod"),
              ]
                .join(" ")
                .trim()}
            >
              MOD
            </span>
            <span
              className={[
                Styles.scoreButton,
                Styles.scoreButtonPegHigh,
                getPegActiveClass("high"),
              ]
                .join(" ")
                .trim()}
            >
              HIGH
            </span>
          </div>
        )}
      </div>
    </div>
  );
};

export default NewScoreSection;
